import { Slideout } from '@jbhi-fi/components/src/Slideout/Slideout';
import React from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { getOfferingForCartItem, JbCartItemTransformer } from './transformers/JbCartItemTransformer';
import { CartLineItem } from '@jbhi-fi/components/src/Cart/CartLineItem/CartLineItem';
import { WishlistCard } from '@jbhi-fi/components/src/Cart/WishlistCard/WishlistCard';
import ProductService from '../pdp/productService/ProductService';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getServiceOfferings } from '../../../utils/parseServiceOfferings';
import { styles } from './MiniCart.css';
import { CartActions } from './CartActions/CartActions';
import CartIcon from '@jbhi-fi/lanyard-ui/components/Icons/Cart';
import { BundleOffering } from '@jbhi-fi/components/src/Cart/BundleOffering/BundleOffering';
import { AttachedService } from '@jbhi-fi/components/src/Cart/AttachedService/AttachedService';

export const MiniCart = () => {
  const {
    isMiniCartOpen,
    toggleMiniCart,
    cart,
    location,
    removeItem,
    updateQuantity,
    addService,
    clearWishlistCards,
    addBundleOffering,
    removeAttachedService,
  } = useCartContext();
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const cartServiceOfferings = getServiceOfferings();
  const isDesktop = useBreakpoint('md', 'up');

  if (location === 'cart') return null;

  return (
    <Slideout
      size={isDesktop ? '25rem' : '100%'}
      isOpen={isMiniCartOpen}
      onClose={() => toggleMiniCart(false)}
      dataTestId="mini-cart-slideout"
      headerElement={
        <h3 className={styles.title} data-testid="miniCart-header">
          My Cart
        </h3>
      }
    >
      {cart?.items.length ? (
        <>
          <div className={styles.lineItems}>
            {cart?.items
              .filter(item => !item.hide)
              .map(item => {
                const offering = getOfferingForCartItem(item, cartServiceOfferings);
                const attachedService = JbCartItemTransformer.toAttachedServiceProps(item);
                const bundleSuggestion = JbCartItemTransformer.toBundleOfferingProps(item);

                return (
                  <CartLineItem
                    key={item.line_id}
                    {...JbCartItemTransformer.toCartLineItemProps(item, cart.isValidated)}
                    bundleOfferings={
                      bundleSuggestion ? (
                        <BundleOffering
                          key={item.line_id}
                          location={location}
                          {...bundleSuggestion}
                          onAdd={() => addBundleOffering(item)}
                        />
                      ) : null
                    }
                    attachedServices={
                      attachedService ? (
                        <AttachedService
                          key={item.line_id}
                          {...attachedService}
                          onRemove={() => item.attachedService && removeAttachedService(item)}
                          variant="miniCart"
                        />
                      ) : undefined
                    }
                    onRemove={() => removeItem(item, isLoggedIn)}
                    variant="miniCart"
                    onQuantityChange={quantity => updateQuantity(item, quantity)}
                    serviceOfferings={
                      offering ? (
                        <ProductService
                          productSku={item.sku}
                          serviceType={offering?.serviceData.typeId}
                          suggestions={[offering.fullOffering]}
                          onAdd={() => addService(item, offering.serviceData)}
                          gtmData={item.onlineProduct?.ProductTags?.GoogleTagManager}
                          shouldHideHeading
                          trackingMessage={offering.trackingMessage}
                        />
                      ) : undefined
                    }
                    wishlistCard={
                      isLoggedIn ? (
                        <WishlistCard
                          {...JbCartItemTransformer.toCartItemWishlistProps(item)}
                          location={location}
                          isSaved={getIsInWishlist(item.sku)}
                          onClose={() => clearWishlistCards()}
                          onWishlistClick={async () => {
                            await handleUpdateWishlist({
                              actionType: 'addItem',
                              sku: item.sku,
                              variantId: Number(item.id),
                            });
                          }}
                          variant="miniCart"
                        />
                      ) : undefined
                    }
                  />
                );
              })}
          </div>
          <div className={styles.summary}>
            <CartActions
              totalPrice={cart?.total_price || 0}
              totalDiscount={cart?.total_discount || 0}
              variant="miniCart"
            />
          </div>
        </>
      ) : (
        <div className={styles.emptyCart} data-testid="miniCart-empty">
          <CartIcon width={32} height={32} />
          <p>Your cart is empty</p>
        </div>
      )}
    </Slideout>
  );
};
